import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { CongressWatch } from '../components/legislative/CongressWatch';
import { LegalOrder } from '../components/legislative/LegalOrder';
import { CountryCompare } from '../components/legislative/CountryCompare';
import {
  Shield,
  ArrowLeft,
  Newspaper,
  AlertTriangle,
  TrendingUp,
  Clock,
  ExternalLink,
  Loader2,
  Filter
} from 'lucide-react';

const impactColors = {
  alto: 'status-error',
  medio: 'status-warning',
  bajo: 'status-success'
};

const LegalFeedPage = () => {
  const { api } = useAuth();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'congreso');

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const response = await api.get('/oracle/feed');
      setFeed(response.data);
    } catch (error) {
      console.error('Error fetching feed:', error);
      toast.error('Error al cargar el feed legal');
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = selectedCategory === 'all'
    ? feed?.news
    : feed?.news?.filter(n => n.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Hace menos de 1 hora';
    if (diffHours < 24) return `Hace ${diffHours} horas`;
    if (diffHours < 48) return 'Ayer';
    return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">Actualidad Jurídica</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-['Outfit']">Actualizaciones Legales</h1>
              <p className="text-sm text-slate-400">Mantente al día con los cambios normativos</p>
            </div>
          </div>
          
          {/* Disclaimer */}
          {tab === 'noticias' && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-amber-200">
                  <strong>NOTA:</strong> Este es un feed de noticias simulado con fines demostrativos. 
                  En producción, se integrará con fuentes de noticias legales en tiempo real.
                </p>
              </div>
            </div>
          </div>
          )}
        </div>

        <div className="mb-6 flex flex-wrap gap-2" role="tablist">
          {[['congreso', 'Congreso y Asambleas'], ['ordenamiento', 'Ordenamiento jurídico'], ['comparador', 'Comparador de países'], ['noticias', 'Noticias legales']].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} data-testid={`feed-tab-${id}`} onClick={() => setTab(id)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${tab === id ? 'border-amber-400 bg-amber-500 text-slate-950' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'congreso' ? <CongressWatch /> : tab === 'ordenamiento' ? <LegalOrder /> : tab === 'comparador' ? <CountryCompare /> : loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-500 mb-4" />
            <p className="text-slate-400">Cargando actualizaciones legales...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar - Categories */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-xl p-4 sticky top-24">
                <div className="flex items-center space-x-2 mb-4">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <h3 className="font-medium">Categorías</h3>
                </div>
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    data-testid="feed-category-all"
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    Todas ({feed?.total_items || 0})
                  </button>
                  {feed?.categories?.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      data-testid={`feed-category-${cat.id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      {cat.name} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* News Feed */}
            <div className="lg:col-span-3 space-y-4">
              {filteredNews?.map((news, index) => (
                <article
                  key={news.id}
                  data-testid={`news-item-${news.id}`}
                  className="bg-card border border-border rounded-xl p-6 card-hover animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={impactColors[news.impact]}>
                          <TrendingUp className="w-3 h-3 inline mr-1" />
                          Impacto {news.impact}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(news.published_at)}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-semibold font-['Outfit'] mb-2 hover:text-blue-400 transition-colors">
                        {news.title}
                      </h3>
                      
                      <p className="text-sm text-slate-400 mb-4">{news.summary}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                          {news.relevant_contracts?.map((contract) => (
                            <span key={contract} className="text-xs bg-slate-800 px-2 py-1 rounded">
                              {contract}
                            </span>
                          ))}
                        </div>
                        <a
                          href={news.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <span>{news.source}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </article>
              ))}

              {filteredNews?.length === 0 && (
                <div className="text-center py-12">
                  <Newspaper className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">No hay noticias en esta categoría</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LegalFeedPage;
